import React, { useState } from 'react';
import './AvailabilityModal.css';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

export type ConstraintReason = 'travel' | 'recovery' | 'reduced' | 'unavailable';

export interface WeekConstraint {
  reason: ConstraintReason | null;       // null = a normal week
  maxMileage: number | null;             // optional explicit cap
  note: string;                          // free text for the AI coach ('' = none)
}

const CONSTRAINT_OPTIONS: { value: ConstraintReason; label: string; hint: string }[] = [
  { value: 'travel',      label: 'Travelling',  hint: 'Easy running only, run what you can' },
  { value: 'recovery',    label: 'Recovery',    hint: 'Light week to absorb the last block' },
  { value: 'reduced',     label: 'Reduced',     hint: 'Fewer / shorter runs than normal' },
  { value: 'unavailable', label: 'Off',         hint: 'Little to no running this week' },
];

export type DayRole = 'run' | 'strength' | 'cycling' | 'walking' | 'rest';

export interface AvailabilitySelection {
  runDays: string[];
  strengthDays: string[];
  cyclingDays: string[];
  walkingDays: string[];
  restDays: string[];
}

const ROLE_OPTIONS: { value: DayRole; label: string }[] = [
  { value: 'run',      label: '🏃 Run' },
  { value: 'strength', label: '💪 Strength' },
  { value: 'cycling',  label: '🚴 Cycling' },
  { value: 'walking',  label: '🚶 Walking' },
  { value: 'rest',     label: '😴 Rest' },
];

interface Props {
  isRegeneration: boolean;
  weekStartDate?: string;
  completedDays?: string[];
  defaultRunDays: string[];
  defaultStrengthDays: string[];
  defaultCyclingDays: string[];
  defaultWalkingDays: string[];
  defaultRestDays: string[];
  defaultConstraintReason?: ConstraintReason | null;
  defaultMaxMileage?: number | null;
  defaultNote?: string | null;
  onConfirm: (selection: AvailabilitySelection, constraint: WeekConstraint) => void;
  onCancel: () => void;
}

const AvailabilityModal: React.FC<Props> = ({
  isRegeneration,
  weekStartDate,
  completedDays = [],
  defaultRunDays,
  defaultStrengthDays,
  defaultCyclingDays,
  defaultWalkingDays,
  defaultRestDays,
  defaultConstraintReason = null,
  defaultMaxMileage = null,
  defaultNote = null,
  onConfirm,
  onCancel,
}) => {
  const [runDays, setRunDays]           = useState<string[]>(defaultRunDays);
  const [strengthDays, setStrengthDays] = useState<string[]>(defaultStrengthDays);
  const [cyclingDays, setCyclingDays]   = useState<string[]>(defaultCyclingDays);
  const [walkingDays, setWalkingDays]   = useState<string[]>(defaultWalkingDays);
  const [restDays, setRestDays]         = useState<string[]>(defaultRestDays);
  const [constraintReason, setConstraintReason] = useState<ConstraintReason | null>(defaultConstraintReason);
  const [note, setNote] = useState<string>(defaultNote || '');
  const [maxMileage, setMaxMileage]     = useState<string>(defaultMaxMileage != null ? String(defaultMaxMileage) : '');

  const getDayRole = (day: string): DayRole | '' => {
    if (runDays.includes(day))      return 'run';
    if (strengthDays.includes(day)) return 'strength';
    if (cyclingDays.includes(day))  return 'cycling';
    if (walkingDays.includes(day))  return 'walking';
    if (restDays.includes(day))     return 'rest';
    return '';
  };

  const setDayRole = (day: string, role: DayRole | '') => {
    // Remove from every role first, then assign the chosen one (unassign on '').
    setRunDays(d => d.filter(x => x !== day));
    setStrengthDays(d => d.filter(x => x !== day));
    setCyclingDays(d => d.filter(x => x !== day));
    setWalkingDays(d => d.filter(x => x !== day));
    setRestDays(d => d.filter(x => x !== day));
    if (role === 'run')      setRunDays(d => [...d, day]);
    if (role === 'strength') setStrengthDays(d => [...d, day]);
    if (role === 'cycling')  setCyclingDays(d => [...d, day]);
    if (role === 'walking')  setWalkingDays(d => [...d, day]);
    if (role === 'rest')     setRestDays(d => [...d, day]);
  };

  const handleConfirm = () => {
    const parsedMax = maxMileage.trim() === '' ? null : Number(maxMileage);
    onConfirm({ runDays, strengthDays, cyclingDays, walkingDays, restDays }, {
      reason: constraintReason,
      maxMileage: constraintReason && parsedMax != null && !Number.isNaN(parsedMax) ? parsedMax : null,
      note: note.trim(),
    });
  };

  const formatWeekLabel = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="avail-overlay" onClick={onCancel}>
      <div className="avail-modal" onClick={e => e.stopPropagation()}>
        <div className="avail-header">
          <h3>
            {isRegeneration ? 'Regenerate plan' : 'Generate next week\'s plan'}
            {weekStartDate && (
              <span className="avail-week-label"> — Week of {formatWeekLabel(weekStartDate)}</span>
            )}
          </h3>
          <p className="avail-subtitle">
            {isRegeneration && completedDays.length > 0
              ? 'Adjust availability for remaining days. Completed workouts are locked.'
              : 'Confirm your availability for this week. Changes here won\'t affect your default schedule.'}
          </p>
        </div>

        <div className="avail-days">
          {ALL_DAYS.map(day => {
            const role = getDayRole(day);
            const isCompleted = completedDays.includes(day);
            return (
              <div key={day} className={`avail-day ${role ? `avail-day--${role}` : ''} ${isCompleted ? 'avail-day--completed' : ''}`}>
                <span className="avail-day-name">{DAY_SHORT[day]}</span>
                {isCompleted ? (
                  <span className="avail-completed-badge">Done</span>
                ) : (
                  <select
                    className={`avail-role-select avail-role-select--${role || 'none'}`}
                    value={role}
                    onChange={e => setDayRole(day, e.target.value as DayRole | '')}
                  >
                    <option value="">— Unassigned</option>
                    {ROLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>

        <div className="avail-constraint">
          <div className="avail-constraint-label">Reduced week? <span className="avail-constraint-opt">(optional)</span></div>
          <div className="avail-constraint-opts">
            <button
              className={`avail-constraint-btn ${constraintReason === null ? 'active' : ''}`}
              onClick={() => setConstraintReason(null)}
            >Normal</button>
            {CONSTRAINT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`avail-constraint-btn ${constraintReason === opt.value ? 'active' : ''}`}
                onClick={() => setConstraintReason(opt.value)}
                title={opt.hint}
              >{opt.label}</button>
            ))}
          </div>
          {constraintReason && (
            <div className="avail-constraint-detail">
              <p className="avail-constraint-hint">
                {CONSTRAINT_OPTIONS.find(o => o.value === constraintReason)?.hint}.
                {' '}No quality or long run this week, and it won't drag down your build.
              </p>
              {constraintReason !== 'unavailable' && (
                <label className="avail-constraint-cap">
                  Cap weekly miles (optional):
                  <input
                    type="number" min="0" step="1" inputMode="numeric"
                    value={maxMileage}
                    onChange={e => setMaxMileage(e.target.value)}
                    placeholder="auto"
                  />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="avail-note">
          <label className="avail-constraint-label" htmlFor="avail-note">
            Anything the coach should know this week? <span className="avail-constraint-opt">(optional)</span>
          </label>
          <textarea
            id="avail-note"
            rows={2}
            maxLength={500}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Busy week — Tue and Thu I can't run more than 30 min."
          />
        </div>

        <div className="avail-summary">
          <span className="avail-chip avail-chip--run">{runDays.length} run day{runDays.length !== 1 ? 's' : ''}</span>
          {strengthDays.length > 0 && (
            <span className="avail-chip avail-chip--strength">{strengthDays.length} strength</span>
          )}
          {cyclingDays.length > 0 && (
            <span className="avail-chip avail-chip--cross">{cyclingDays.length} cycling</span>
          )}
          {walkingDays.length > 0 && (
            <span className="avail-chip avail-chip--cross">{walkingDays.length} walking</span>
          )}
          {restDays.length > 0 && (
            <span className="avail-chip avail-chip--rest">{restDays.length} rest</span>
          )}
          {completedDays.length > 0 && (
            <span className="avail-chip avail-chip--completed">{completedDays.length} done</span>
          )}
        </div>

        <div className="avail-actions">
          <button className="avail-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="avail-btn-confirm"
            onClick={handleConfirm}
            disabled={runDays.length === 0 && constraintReason !== 'unavailable'}
          >
            Generate Plan →
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
